import { useMemo } from 'react';

import type { ReportGridFilterModel } from '@aline/common';
import { isEqual } from 'lodash';
import { Apollo } from 'src/gql/generated';
import { useDebouncedValue } from 'src/hooks/utils';

const COUNT_DEBOUNCE_MS = 500;

interface CountInput {
  readonly filterModel: ReportGridFilterModel | undefined;
  readonly includeDrafts: boolean;
}

/**
 * The documents a run card would run on under the filters the user set,
 * counted the same way the approved run selects them. The proposal's own count
 * covers only its existing questions under its own filters, so it stands until
 * the user changes the filters; drafts, which have run nowhere, are always
 * counted. A card saved without filters is counted on the proposal's filters.
 * `loading` holds while the current filters and drafts are waiting on their
 * count, and `failed` says the count did not come back, so the run must be
 * confirmed.
 */
export function useQuestionRunCandidateCount({
  enabled,
  filterModel,
  includeDrafts,
  initialCount,
  initialFilterModel,
  messageId,
  runProposalId,
}: {
  enabled: boolean;
  filterModel: ReportGridFilterModel | undefined;
  includeDrafts: boolean;
  initialCount: number;
  initialFilterModel: ReportGridFilterModel | undefined;
  messageId: string;
  runProposalId: string;
}) {
  // Debounced together, so a result for other drafts or filters never stands for these.
  const countInput = useMemo<CountInput>(
    () => ({ filterModel, includeDrafts }),
    [filterModel, includeDrafts]
  );
  const debouncedInput = useDebouncedValue(countInput, COUNT_DEBOUNCE_MS);
  const { data, error, loading } = Apollo.useCountQuestionRunCandidatesQuery({
    fetchPolicy: 'network-only',
    skip: !enabled || !getNeedsCount(debouncedInput, initialFilterModel),
    variables: { ...debouncedInput, messageId, runProposalId },
  });

  if (!getNeedsCount(countInput, initialFilterModel))
    return { count: initialCount, failed: false, loading: false };
  const count = data?.countQuestionRunCandidates ?? initialCount;
  // A result from an earlier edit says nothing about this one, so a pending edit keeps the run held.
  if (loading || debouncedInput !== countInput) return { count, failed: false, loading: true };
  // A failed count must not hold the run forever, but the run then asks for confirmation.
  if (error) return { count: initialCount, failed: true, loading: false };
  return { count, failed: false, loading: !data };
}

function getNeedsCount(
  { filterModel, includeDrafts }: CountInput,
  initialFilterModel: ReportGridFilterModel | undefined
) {
  return includeDrafts || !isEqual(filterModel, initialFilterModel);
}
